import { JsonRpcProvider } from 'ethers'

import { BUNDLER } from '../consts/bundlers'
import {
  AMBIRE_ACCOUNT_FACTORY,
  PROXY_AMBIRE_ACCOUNT,
  ZKX_SA_DEPLOYED_CHAIN_IDS
} from '../consts/deploy'
import {
  ChainlistNetwork,
  Erc4337settings,
  Network,
  NetworkFeature,
  RelayerNetwork
} from '../interfaces/network'
import { RPCProvider } from '../interfaces/provider'
import { byZkxEnv } from './zkxEnv'

const hardcodedRpcUrls: { [chainId: string]: string } = {
  '11155111': 'https://eth-sepolia.public.blastapi.io'
}

// Backend host for ZKX-owned proxies (cena price feed, invictus RPC). Follows
// the single `ENV_MODE` switch (utils/zkxEnv), so it can no longer disagree with the
// host the app's own config resolved — it used to read `MOBILE_WEBAPP_DEV`
// itself, and a build whose app code was pinned to prod still sent price/RPC
// traffic to the dev host.
//
// Keep in step with `ZKX_API_DEV_HOST` / `ZKX_API_PROD_HOST` in the app's
// `common/config/env/env.ts`: that module rewrites the `.env` relayer/velcro
// URLs between the same two hosts, and the submodule cannot import from it.
export const ZKX_API_BASE = byZkxEnv({
  dev: 'https://apis.zkxteam.xyz',
  uat: 'https://apis.zkxwallet.com',
  prod: 'https://apis.zkxwallet.com'
})

/**
 * Every host the ZKX invictus RPC proxy has ever been served from, newest last.
 *
 * Deliberately NOT `ZKX_API_BASE` alone: this predicate classifies an rpcUrl
 * that may have come out of STORAGE, written by a build of a different vintage
 * or environment. Treating an older host as "some custom RPC the user added"
 * silently re-enables request batching on it and offers a Remove button for an
 * RPC the user never added, so recognition has to span all of them.
 */
const ZKX_INVICTUS_PROXY_HOSTS = [
  'https://apis.zkxwallet.com',
  'https://apis.dev.zkxwallet.com',
  'https://apis.zkxteam.xyz'
]

/** The invictus RPC — Ambire's own host, or any ZKX same-origin proxy of it. */
export const isZkxInvictusRpcUrl = (rpcUrl: string): boolean =>
  rpcUrl.includes('invictus.ambire.com') ||
  ZKX_INVICTUS_PROXY_HOSTS.some((host) => rpcUrl.includes(`${host}/invictus`))

const checkIsRpcUrlWorking = async (rpcUrl: string) => {
  const provider = new JsonRpcProvider(rpcUrl)

  try {
    await provider.getBlockNumber()
  } catch {
    provider?.destroy()
    return false
  }

  provider.destroy()

  return true
}

const rollProviderUrlsAndFindWorking = async (
  rpcUrls: string[],
  index: number
): Promise<string | null> => {
  const isProviderWorking = await checkIsRpcUrlWorking(rpcUrls[index])

  if (isProviderWorking) {
    return rpcUrls[index]
  }

  const nextIndex = index + 1

  if (rpcUrls.length > nextIndex) {
    return rollProviderUrlsAndFindWorking(rpcUrls, nextIndex)
  }

  return null
}

const convertToAmbireNetworkFormat = async (network: ChainlistNetwork): Promise<Network> => {
  const freeHttpRpcUrls = network.rpc.filter((rpcUrl: string) => {
    const isHttpOrHttps = rpcUrl.startsWith('http')

    if (!isHttpOrHttps) return false

    const isApiKeyRequired = /${.+}/.test(rpcUrl)

    return !isApiKeyRequired
  })
  const workingRpcUrl: string =
    hardcodedRpcUrls[network.chainId.toString()] ??
    (await rollProviderUrlsAndFindWorking(freeHttpRpcUrls, 0))

  let platformId = null
  let nativeAssetId = null

  try {
    const coingeckoRequest = await fetch(
      `${ZKX_API_BASE}/cena/api/v3/platform/${Number(network.chainId)}`
    ).catch(() => ({
      error: 'currently, we cannot fetch the coingecko information'
    }))

    // set the coingecko info

    if (!('error' in coingeckoRequest)) {
      const coingeckoInfo = await coingeckoRequest.json()
      if (!coingeckoInfo.error) {
        platformId = coingeckoInfo.platformId
        nativeAssetId = coingeckoInfo.nativeAssetId
      }
    }
  } catch (e) {
    console.error(e)
    // do nothing
  }

  return {
    name: network.name,
    chainId: BigInt(network.chainId),
    rpcUrls: [workingRpcUrl ?? network.rpc[0]],
    explorerUrl: network.explorers[0]?.url || '',
    selectedRpcUrl: workingRpcUrl || '',
    platformId,
    nativeAssetId,
    nativeAssetSymbol: network.nativeCurrency.symbol,
    nativeAssetName: network.nativeCurrency.name,
    // Not needed for benzin
    hasRelayer: false,
    rpcNoStateOverride: false, // TODO
    areContractsDeployed: false, // TODO
    features: [],
    feeOptions: { is1559: false },
    flagged: false,
    hasSingleton: false,
    iconUrls: [],
    erc4337: { enabled: false, hasPaymaster: false },
    isSAEnabled: false,
    predefined: false,
    has7702: false
  }
}

/**
 * Maps the configuration of a Relayer network to the Ambire network format.
 * Needed, because the structures does NOT fully match, some values need to be
 * transformed or parsed (number to bigint). And finally, because there are
 * default values that need to be set for the so called "predefined" networks.
 */
const ZKX_PROXY_INVICTUS = `${ZKX_API_BASE}/invictus`
const rewriteAmbireRpcUrl = (url: string): string =>
  typeof url === 'string'
    ? url.replace(/^https:\/\/invictus\.ambire\.com/, ZKX_PROXY_INVICTUS)
    : url

export const mapRelayerNetworkConfigToAmbireNetwork = (
  chainId: bigint,
  relayerNetwork: RelayerNetwork
): Network & { predefinedConfigVersion: number; disabledByDefault?: boolean } => {
  const {
    name,
    explorerUrl,
    selectedRpcUrl: rawSelectedRpcUrl,
    isOptimistic,
    disableEstimateGas,
    predefinedConfigVersion,
    rpcUrls: rawRpcUrls,
    iconUrls,
    platformId,
    has7702,
    disabledByDefault,
    rpcNoStateOverride
  } = relayerNetwork
  const {
    native: {
      symbol: nativeAssetSymbol,
      name: nativeAssetName,
      coingeckoId: nativeAssetId,
      wrapped,
      oldNativeAssetSymbols
    },
    smartAccounts,
    feeOptions: incomingFeeOptions
  } = relayerNetwork

  // `wrapped` is optional: a chain can have no wrapped-native token at all
  // (Arc/5042 - its native USDC is reachable through an ERC-20 predeploy that
  // is a view of the same balance, not a WETH9 wrapper). Everything that reads
  // `wrappedAddr` treats it as "there is a 1:1 WETH-style contract here":
  // `libs/transfer/userRequest.ts` encodes a WETH `deposit()` against it for a
  // native gas-tank top-up, `libs/gasTank/topUpAssets.ts` matches top-up assets
  // by it, `consts/coingecko.ts` prices the native asset through it. A wrong or
  // placeholder address would send value to a contract that cannot give it
  // back, so `undefined` - which every one of those call sites already guards
  // for - is the only correct value when the chain has no wrapper.
  const wrappedAddr = wrapped?.address

  const is7702Enabled = has7702 || false
  const selectedRpcUrl = rewriteAmbireRpcUrl(rawSelectedRpcUrl)
  const rpcUrls = Array.isArray(rawRpcUrls) ? rawRpcUrls.map(rewriteAmbireRpcUrl) : rawRpcUrls

  const hasRelayer = smartAccounts?.hasRelayer ?? false
  const incomingErc4337 = smartAccounts?.erc4337 ?? { enabled: false, hasPaymaster: false }

  const feeOptions = {
    is1559: incomingFeeOptions.is1559,
    minBaseFeeEqualToLastBlock: !!incomingFeeOptions.minBaseFeeEqualToLastBlock,
    ...(typeof incomingFeeOptions.minBaseFee === 'number' && {
      minBaseFee: BigInt(incomingFeeOptions.minBaseFee)
    }),
    ...(typeof incomingFeeOptions.elasticityMultiplier === 'number' && {
      elasticityMultiplier: BigInt(incomingFeeOptions.elasticityMultiplier)
    }),
    ...(typeof incomingFeeOptions.baseFeeMaxChangeDenominator === 'number' && {
      baseFeeMaxChangeDenominator: BigInt(incomingFeeOptions.baseFeeMaxChangeDenominator)
    }),
    ...(typeof incomingFeeOptions.feeIncrease === 'number' && {
      feeIncrease: BigInt(incomingFeeOptions.feeIncrease)
    })
  }

  const erc4337: Erc4337settings = {
    enabled: incomingErc4337.enabled,
    hasPaymaster: incomingErc4337.hasPaymaster,
    ...(typeof incomingErc4337.hasBundlerSupport === 'boolean' && {
      hasBundlerSupport: incomingErc4337.hasBundlerSupport
    }),
    ...(incomingErc4337.bundlers && {
      bundlers: incomingErc4337.bundlers as BUNDLER[]
    }),
    ...(incomingErc4337.defaultBundler && {
      defaultBundler: incomingErc4337.defaultBundler
    }),
    ...(incomingErc4337.increasePreVerGas && {
      increasePreVerGas: incomingErc4337.increasePreVerGas ?? 0
    })
  }

  // Always fallback to these values for the "predefined" networks, coming from
  // the RPC for the custom networks.
  //
  // ZKX funds-safety defense-in-depth: the backend must never be able to arm
  // Smart Accounts on a chain where the ZKX factory is not deployed. The prod
  // backend still advertises `smartAccounts` for upstream Ambire chains we never
  // deployed to (137/42161/10/…), and our static predefined networks carry
  // `predefinedConfigVersion: 0`, so this backend config would otherwise win the
  // merge and force `isSAEnabled/areContractsDeployed = true` → a user could
  // fund an undeployable SA there (stuck funds). Intersect against the deployed
  // allowlist so the flags can only ever be enabled on 1/56/8453/369, whatever
  // the backend says. (Custom networks added by the user go through a different
  // code path and are unaffected.)
  // This is a FLOOR, not the final answer: the backend can never arm SA here on
  // its own, but `NetworksController.#probeSaDeploymentsOnUnlistedChains` may
  // later flip these to `true` for a candidate chain after reading the factory
  // and proxy code on-chain (`probeZkxSaContractsDeployed`). That keeps the
  // funds-safety property — only a real deployment enables SA — while letting a
  // chain the backend deployed to after this build shipped light up without a
  // release. Never widen the condition below to trust `smartAccounts` alone.
  const isZkxSaDeployedChain = ZKX_SA_DEPLOYED_CHAIN_IDS.includes(chainId)
  const isSAEnabled = !!smartAccounts && isZkxSaDeployedChain
  const areContractsDeployed = !!smartAccounts && isZkxSaDeployedChain
  const features: NetworkFeature[] = []
  const hasSingleton = true

  // Coming from the RPC, only for the custom networks
  // const flagged
  // const blockGasLimit
  // const force4337

  return {
    name,
    iconUrls,
    explorerUrl,
    rpcUrls,
    selectedRpcUrl,
    isOptimistic,
    disableEstimateGas,
    platformId,
    chainId,
    nativeAssetSymbol,
    nativeAssetName,
    nativeAssetId,
    hasRelayer,
    suggestedRpcUrl: selectedRpcUrl,
    suggestedRpcBatchCount: relayerNetwork.selectedRpcBatchCount,
    // Spread conditionally: the merge in `getNetworksUpdatedWithRelayerNetworks`
    // spreads this object over the predefined/stored network, so emitting
    // `wrappedAddr: undefined` would ERASE a correct address that
    // `consts/networks.ts` already carries for a chain whose relayer entry
    // simply omits it. Absent means "no opinion", not "no wrapper".
    ...(wrappedAddr !== undefined && { wrappedAddr }),
    oldNativeAssetSymbols,
    feeOptions,
    erc4337,
    rpcNoStateOverride: rpcNoStateOverride !== undefined ? rpcNoStateOverride : false,
    rpcNoStateOverrideByConfig: rpcNoStateOverride === true,
    isSAEnabled,
    predefined: !disabledByDefault,
    predefinedConfigVersion,
    areContractsDeployed,
    features,
    hasSingleton,
    has7702: is7702Enabled,
    disabledByDefault
  }
}

/**
 * Would enabling Smart Accounts on `chainId` require trusting the backend?
 *
 * `ZKX_SA_DEPLOYED_CHAIN_IDS` is the fast path: chains we know we deployed to,
 * no RPC round-trip needed. Everything else the relayer advertises is a
 * CANDIDATE — it may be a chain the backend deployed to after this build was
 * cut, or a chain that was never ours (the prod relayer advertises
 * `smartAccounts` for all 39 upstream Ambire chains, 35 of which have no ZKX
 * factory). Only `probeZkxSaContractsDeployed` can tell the two apart.
 */
export const isZkxSaProbeCandidate = (network: Network): boolean =>
  !ZKX_SA_DEPLOYED_CHAIN_IDS.includes(network.chainId) &&
  !network.areContractsDeployed &&
  // The relayer has to claim SA support in the first place; probing a chain it
  // never offered would enable a surface nothing else in the app expects.
  (!!network.hasRelayer || !!network.erc4337?.enabled)

/**
 * Is the ZKX Smart Account factory ACTUALLY deployed on the chain behind
 * `provider`? This is the on-chain answer to the question the backend config
 * only claims an answer to, and it is the ONLY thing allowed to arm Smart
 * Accounts on a chain outside `ZKX_SA_DEPLOYED_CHAIN_IDS`.
 *
 * Both contracts are required: the factory deploys the account, the proxy is
 * the implementation it deploys. A chain with one but not the other would hand
 * the user an address that still cannot be deployed — the exact stuck-funds
 * outcome the allowlist exists to prevent.
 */
export const probeZkxSaContractsDeployed = async (provider: RPCProvider): Promise<boolean> => {
  const [factoryCode, proxyCode] = await Promise.all([
    provider.getCode(AMBIRE_ACCOUNT_FACTORY),
    provider.getCode(PROXY_AMBIRE_ACCOUNT)
  ])

  return factoryCode !== '0x' && proxyCode !== '0x'
}

export { checkIsRpcUrlWorking, convertToAmbireNetworkFormat, rollProviderUrlsAndFindWorking }
